import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  streamMessage, getConversations, newConversation,
  getConversationMessages, deleteConversation as deleteConversationApi,
  renameConversation as renameConversationApi, getQuota,
  chatIntentBroadcast, createBroadcast, chatIntentMeet,
} from '../api/guru';
import { useAuth } from './AuthContext';

// Chat session state — conversations, the active conversation's messages,
// the in-flight "loading" flag, and the pending source-picker question — all
// used to live as local useState inside pages/Chat.js. Dev, 2026-07-03: that
// meant navigating to /admin or /projects and back UNMOUNTED the Chat page,
// which threw all of that away. The in-flight `sendMessage` request kept
// running in the background (nothing cancels it) and the backend saved the
// answer just fine (see conversation_store.py), but the ANSWER — and the
// "loading…" indicator itself — vanished from the UI because nothing was
// still listening when it resolved: the promise's .then()/setState calls
// were bound to a component instance that no longer existed. Coming back to
// Chat re-mounted it from scratch, showing whatever was last saved at that
// point (often just the user's question, no reply yet).
//
// Moving this state up into a Context that's mounted once, above the
// <Routes>, fixes it: navigating away no longer unmounts anything holding
// this state, so an in-flight request's response updates the SAME state
// Chat.js reads from, whether or not Chat.js happens to be on screen right
// now. Jump to Projects, come back, and the finished answer (or the
// "loading" spinner still ticking) is right there.
const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId]           = useState(null);
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState('');
  const [loading, setLoading]             = useState(false);
  // Dev, 2026-07-04: what step the backend is currently on ("Checking
  // Google Search Console", "Searching the web", ...) while a question is
  // in flight — see streamMessage()'s onStatus callback below. '' means
  // "no request in flight yet" (loading may still briefly be true right
  // before the first status event arrives).
  // statusSteps: array of {label, done} — builds up as each tool step fires
  const [statusSteps, setStatusSteps]     = useState([]);
  const stepsRef = useRef([]);  // mirror for reading inside callbacks without stale closure
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [quota, setQuota] = useState(null);
  const [darkTheme, setDarkTheme] = useState(
    () => localStorage.getItem('guru-theme') === 'dark'
  );
  // Apply dark theme to document.body so it covers ALL pages globally
  useEffect(() => {
    document.body.classList.toggle('dark-theme', darkTheme);
    localStorage.setItem('guru-theme', darkTheme ? 'dark' : 'light');
  }, [darkTheme]);

  const [initializing, setInitializing] = useState(true); // true until first conv restore attempt
  const initedRef = useRef(false); // guards the one-time initial load below

  const loadQuota = useCallback(() => {
    getQuota().then(({ data }) => setQuota(data)).catch(() => {});
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await getConversations();
      setConversations(data.conversations || []);
      return data.conversations || [];
    } catch { return []; }
  }, []);

  const openConversation = useCallback(async (id) => {
    if (id === activeId) return; // already open — don't flicker
    setActiveId(id);          // highlight sidebar immediately
    setPendingQuestion(null);
    // Don't clear messages before the fetch — replace them atomically so
    // there's no empty-state flash while the request is in flight.
    try {
      const { data } = await getConversationMessages(id);
      const msgs = data.messages || [];
      // Restore any timeline steps saved for this conversation.
      try {
        const saved = JSON.parse(sessionStorage.getItem(`guru-steps-${id}`) || '{}');
        if (Object.keys(saved).length > 0) {
          msgs.forEach((m, i) => {
            if (m.role === 'assistant' && saved[i] !== undefined) {
              m.timelineSteps = saved[i];
            }
          });
        }
      } catch {}
      // Restore KB source IDs so "✅ Added to Knowledge Base" persists across navigation.
      try {
        const savedKb = JSON.parse(sessionStorage.getItem(`guru-kb-${id}`) || '{}');
        if (Object.keys(savedKb).length > 0) {
          msgs.forEach((m, i) => {
            if (m.role === 'assistant' && savedKb[i] !== undefined) {
              m.kbSourceId = savedKb[i];
              m.rating = 'up';
            }
          });
        }
      } catch {}
      setMessages(msgs);
    } catch { setMessages([]); }
  }, [activeId]);

  // Persist activeId to sessionStorage whenever it becomes non-null.
  // We do NOT clear on null here — that would race with the init effect on
  // refresh (activeId starts null, effect fires, wipes the key before the
  // init effect reads it). Clearing is done explicitly in newChat() and on
  // logout, so the key is only ever removed when the user intentionally
  // starts a new chat or signs out.
  useEffect(() => {
    if (activeId) sessionStorage.setItem('guru-active-conv', activeId);
  }, [activeId]);

  // Clear saved conv on actual logout (user went from set → null).
  // We must NOT clear on initial mount when user is null because auth hasn't
  // resolved yet — that would wipe the key before the init effect reads it.
  const hadUserRef = useRef(false);
  useEffect(() => {
    if (user) {
      hadUserRef.current = true;
    } else if (hadUserRef.current) {
      // Real logout: user was set, now it's null
      sessionStorage.removeItem('guru-active-conv');
      hadUserRef.current = false;
    }
  }, [user]);

  // Load the conversation list exactly once per signed-in session.
  // If sessionStorage holds an id from before the refresh, reopen it;
  // otherwise land on a blank new chat.
  useEffect(() => {
    if (!user || initedRef.current) return;
    initedRef.current = true;
    loadQuota();
    loadConversations().then(async convs => {
      const savedId = sessionStorage.getItem('guru-active-conv');
      if (savedId && convs.some(c => c.id === savedId)) {
        await openConversation(savedId);
      }
      setInitializing(false);
    });
  }, [user, loadQuota, loadConversations, openConversation]);

  const newChat = useCallback(() => {
    // Don't create a conversation until the first message is sent —
    // this prevents empty untitled conversations from piling up in the
    // sidebar whenever the user clicks "New Chat" without typing anything.
    setActiveId(null);
    setMessages([]);
    setPendingQuestion(null);
    setInput('');
    sessionStorage.removeItem('guru-active-conv');
  }, []);

  const deleteConv = useCallback(async (id) => {
    try { sessionStorage.removeItem(`guru-steps-${id}`); } catch {}
    try { sessionStorage.removeItem(`guru-kb-${id}`); } catch {}
    await deleteConversationApi(id);
    const convs = await loadConversations();
    if (activeId === id) {
      if (convs.length > 0) openConversation(convs[0].id);
      else { setActiveId(null); setMessages([]); setPendingQuestion(null); }
    }
  }, [activeId, loadConversations, openConversation]);

  const renameConv = useCallback(async (id, title) => {
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
    try {
      await renameConversationApi(id, title);
    } catch {
      loadConversations();
    }
  }, [loadConversations]);

  const quotaExhausted = !!(quota && !quota.is_admin && quota.remaining <= 0);

  // ── Broadcast intent state ────────────────────────────────────────────────
  const [broadcastDraft, setBroadcastDraft] = useState(null);
  const [meetDraft, setMeetDraft]           = useState(null);
  // meetDraft: { title, meet_link, event_link, start, end, attendee_emails,
  //              attendee_names, email_subject, email_body, drafting, error }

  // Detect Google Meet intent — checked BEFORE broadcast intent
  const _isMeetRequest = (text) => {
    const t = text.trim().toLowerCase();
    const patterns = [
      /\bmeet\s*(link|call|invite|session)\b/,
      /\bsend\b.{0,20}\bmeet\b/,
      /\bschedule\b.{0,20}\b(call|meeting|meet)\b/,
      /\bcreate\b.{0,20}\b(meet|meeting|call)\b/,
      /\bset\s*up\b.{0,20}\b(call|meet|meeting)\b/,
      /\bgoogle\s*meet\b/,
      /\bvideo\s*(call|meeting)\b/,
    ];
    return patterns.some(p => p.test(t));
  };

  // Detect email broadcast intent
  const _isBroadcastRequest = (text) => {
    const t = text.trim().toLowerCase();
    const patterns = [
      /\bsend\b.{0,20}\bmail\b/,
      /\bsend\b.{0,20}\bemail\b/,
      /\bmail\b.{0,10}\bto\b/,
      /\bemail\b.{0,10}\bto\b/,
      /\bmail\b.{0,20}\ball\s*employee/,
      /\bemail\b.{0,20}\ball\s*employee/,
      /\bmail\b.{0,20}\bteam\b/,
      /\bbroadcast\b.{0,20}\b(mail|email|message)\b/,
      /\bnotify\b.{0,20}\ball\s*(employee|team|staff)\b/,
    ];
    return patterns.some(p => p.test(t));
  };

  // Detects messages that are purely asking to export previous results as a Word doc.
  // These bypass the SourcePicker (no data source needed) and don't consume quota.
  //
  // Logic: if there's already a previous answer in the conversation AND the message
  // is short AND mentions a doc/word format AND doesn't look like a new data query
  // → treat it as a doc export request. No giant phrase list needed.
  const _isDocOnlyRequest = (text) => {
    // Never trigger on a fresh conversation — nothing to export yet.
    const hasPreviousResult = messages.some(m => m.role === 'assistant');
    if (!hasPreviousResult) return false;

    const t = text.trim().toLowerCase().replace(/[?.!,]+$/, '');
    const words = t.split(/\s+/);

    // Longer messages are almost certainly real queries, not just "make a doc"
    if (words.length > 7) return false;

    // Must mention a document format
    if (!/\b(doc|docs|document|docx|word|word\s*file|word\s*doc|word\s*report)\b/.test(t)) return false;

    // Reject if the message signals a NEW data query (has client/topic qualifiers)
    const NEW_QUERY_SIGNALS = [
      'for ', 'about ', 'regarding ', ' on ', ' from ',   // topic/client qualifiers
      'strategy', 'plan', 'outline', 'analysis', 'audit', // new task types
      'keyword', 'ranking', 'backlink', 'traffic',         // SEO topics = new fetch
      'top ', 'best ', 'latest ', 'recent ',               // data-qualifying adjectives
      'client', 'project', 'website', 'site ',             // subjects implying new fetch
    ];
    if (NEW_QUERY_SIGNALS.some(w => t.includes(w))) return false;

    return true;
  };

  // Shared tail end of confirmSources()/quickReply()/doc-request path — sends one
  // message to the backend and appends the reply (or an error bubble).
  const sendAndAppend = useCallback(async (text, convId, historyBase, sources, isDocRequest = false) => {
    // Build history for the backend — user/assistant turns only.
    // Broadcast and meet messages are stored with special roles (broadcast_draft,
    // broadcast_sent, meet_draft, meet_sent) and their paired user messages have
    // no assistant reply in the messages array. Without this fix Claude sees those
    // unanswered user messages and tries to re-handle them alongside the real query.
    const ACTION_ROLES = new Set(['broadcast_draft', 'broadcast_sent', 'meet_draft', 'meet_sent']);
    const recentHistory = [];
    for (let i = 0; i < historyBase.length; i++) {
      const m = historyBase[i];
      if (m.role === 'user') {
        recentHistory.push({ role: 'user', content: m.content });
        // If the very next message is an action card (not a real assistant reply),
        // inject a synthetic acknowledgement so Claude knows this was already handled.
        const next = historyBase[i + 1];
        if (next && ACTION_ROLES.has(next.role)) {
          recentHistory.push({ role: 'assistant', content: '✅ Done.' });
        }
      } else if (m.role === 'assistant') {
        recentHistory.push({ role: 'assistant', content: m.content });
      }
    }
    recentHistory.splice(0, Math.max(0, recentHistory.length - 21));

    const init = [{ label: 'Thinking', done: false }];
    stepsRef.current = init;
    setStatusSteps(init);

    await streamMessage(text, convId, recentHistory, sources, {
      // Fires as each backend step starts (Claude round trip, GSC/GA4/
      // Karma/web-search calls, ...) — see guru_chat.py's on_status calls.
      // Purely a progress label; it never touches `messages`/answer content.
      onStatus: (label) => setStatusSteps(prev => {
        const next = [...prev.map(s => ({ ...s, done: true })), { label, done: false }];
        stepsRef.current = next;
        return next;
      }),

      onDone: (data) => {
        const doneSteps = stepsRef.current.map(s => ({ ...s, done: true }));
        stepsRef.current = [];
        setStatusSteps([]);
        setMessages(prev => {
          // Persist steps inside the updater so we know the exact message index
          // this assistant message will occupy. Using prev.length (the index of
          // the new message) as the key is far more reliable than using
          // data.answer as a key — any whitespace/formatting difference between
          // the streamed answer and the API-returned content breaks the lookup.
          try {
            if (convId && doneSteps.length > 0) {
              const key = `guru-steps-${convId}`;
              const existing = JSON.parse(sessionStorage.getItem(key) || '{}');
              existing[prev.length] = doneSteps;
              sessionStorage.setItem(key, JSON.stringify(existing));
            }
          } catch {}
          return [
          ...prev,
          {
            id:              Date.now() + 1,
            role:            'assistant',
            content:         data.answer,
            source:          data.source,
            citations:       data.citations || [],
            selectedSources: sources,
            quickReplies:    data.quick_replies || [],
            timelineSteps:   doneSteps,
          },
        ]});
        loadConversations().then(c => setConversations(c));
        loadQuota();
      },

      // Dev, 2026-07-06: show the REAL error instead of a generic guess, so
      // it's actually debuggable from the chat window itself — no more
      // digging through server logs just to find out what broke. Full
      // detail (type/message/traceback) is always logged to the browser
      // console; the chat bubble shows a shorter version per `kind` (see
      // streamMessage's onError doc comment in api/guru.js for what each
      // kind means).
      onError: ({ kind, status, type, message, traceback: tb }) => {
        console.error('[Guru chat error]', { kind, status, type, message, traceback: tb });

        const isQuotaError = kind === 'http' && status === 429;
        if (isQuotaError) {
          setMessages(prev => [...prev, {
            id: Date.now() + 1, role: 'assistant', source: 'error', citations: [],
            content: `🚫 ${message || 'Daily question limit reached.'}`,
          }]);
          loadQuota();
          return;
        }

        let content;
        if (kind === 'network') {
          content = `⚠️ Could not reach the backend at all: ${message || 'network error'}. `
            + `Check that it's running and that the frontend's API URL/port matches.`;
        } else if (kind === 'incomplete') {
          content = '⚠️ The connection to the backend closed before an answer arrived — '
            + 'often a reverse-proxy/load-balancer timeout on a slow request. '
            + 'Check server/proxy logs (this is not a "backend is down" error).';
        } else {
          // 'http' (non-2xx before streaming started) or 'stream' (chat()
          // threw mid-stream) — both now carry the real exception.
          content = `⚠️ ${type ? `${type}: ` : ''}${message || 'Unknown error'}`
            + (status ? ` (HTTP ${status})` : '');
        }

        setMessages(prev => [...prev, {
          id: Date.now() + 1, role: 'assistant', source: 'error', citations: [], content,
        }]);
      },
    }, isDocRequest);

    stepsRef.current = [];
    setStatusSteps([]);
    setLoading(false);
  }, [loadConversations, loadQuota]);

  // ── Meet draft handlers ───────────────────────────────────────────────────
  const confirmChatMeet = useCallback(async () => {
    if (!meetDraft) return;
    // Event is already created — just send the notification email
    const { email_subject, email_body, attendee_emails } = meetDraft;
    try {
      await createBroadcast(email_subject, email_body, '', null, attendee_emails);
      setMessages(prev => prev.map(m =>
        m.role === 'meet_draft' ? { ...m, role: 'meet_sent' } : m
      ));
      setMeetDraft(null);
    } catch {
      setMeetDraft(prev => ({ ...prev, error: 'Failed to send invite email. Check SMTP config.' }));
    }
  }, [meetDraft]);

  const discardChatMeet = useCallback(() => {
    setMessages(prev => prev.filter(m => m.role !== 'meet_draft'));
    setMeetDraft(null);
  }, []);

  // Confirm and send a broadcast draft from chat
  // overrideSubject / overrideBody let Chat.js pass edited values directly
  // without waiting for setBroadcastDraft to flush (React state is async).
  const confirmChatBroadcast = useCallback(async (scheduledAt = null, overrideSubject = null, overrideBody = null) => {
    if (!broadcastDraft) return;
    const subject   = overrideSubject || broadcastDraft.subject;
    const body_text = overrideBody    || broadcastDraft.body_text;
    const { recipient_mode, recipient_emails } = broadcastDraft;
    const recipients = recipient_mode === 'all' ? null : recipient_emails;
    // Always rebuild body_html from the current body_text so edits are reflected.
    // Tight paragraph margins (6px) prevent the huge gaps Gmail adds with default <p> spacing.
    const body_html = body_text
      .split(/\n\n+/)
      .map(para =>
        `<p style="margin:0 0 6px 0;padding:0;line-height:1.5">${
          para.split('\n').join('<br>')
        }</p>`
      )
      .join('');
    try {
      const isoAt = scheduledAt ? new Date(scheduledAt).toISOString() : null;
      await createBroadcast(subject, body_text, body_html, isoAt, recipients);
      // Replace the draft card with a sent confirmation message
      setMessages(prev => prev.map(m =>
        m.role === 'broadcast_draft'
          ? { ...m, role: 'broadcast_sent', scheduledAt: isoAt }
          : m
      ));
      setBroadcastDraft(null);
    } catch {
      setBroadcastDraft(prev => ({ ...prev, error: 'Failed to send. Check SMTP config.' }));
    }
  }, [broadcastDraft]);

  const discardChatBroadcast = useCallback(() => {
    setMessages(prev => prev.filter(m => m.role !== 'broadcast_draft'));
    setBroadcastDraft(null);
  }, []);

  const updateChatBroadcastBody = useCallback((newBody) => {
    setBroadcastDraft(prev => prev ? { ...prev, body_text: newBody } : prev);
  }, []);

  const updateChatBroadcastSubject = useCallback((newSubject) => {
    setBroadcastDraft(prev => prev ? { ...prev, subject: newSubject } : prev);
  }, []);

  // Step 1: show the source picker for a typed question.
  // Exception 1: doc export requests bypass SourcePicker.
  // Exception 2: broadcast/email-send requests (admin only) bypass SourcePicker
  //   and show a draft confirmation card in the chat instead.
  const askQuestion = useCallback((text) => {
    if (!text || loading || pendingQuestion) return;

    // Meet intent — admin only, checked before broadcast intent
    if (_isMeetRequest(text) && quota?.is_admin) {
      const userMsg  = { role: 'user',       content: text, id: Date.now() };
      const draftMsg = { role: 'meet_draft', content: '',   id: Date.now() + 1 };
      setMessages(prev => [...prev, userMsg, draftMsg]);
      setInput('');
      setLoading(true);
      const meetInit = [{ label: 'Thinking', done: true }, { label: 'Creating Google Meet', done: false }];
      stepsRef.current = meetInit;
      setStatusSteps(meetInit);
      setMeetDraft({ drafting: true, originalMessage: text });
      chatIntentMeet(text)
        .then(r => {
          setMeetDraft({ ...r.data, drafting: false });
          stepsRef.current = [];
          setStatusSteps([]);
          setLoading(false);
        })
        .catch(err => {
          const detail = err?.response?.data?.detail || 'Meet creation failed. Try again.';
          setMeetDraft(prev => ({ ...prev, drafting: false, error: detail }));
          stepsRef.current = [];
          setStatusSteps([]);
          setLoading(false);
        });
      return;
    }

    // Broadcast intent — admin only, bypass SourcePicker entirely
    if (_isBroadcastRequest(text) && quota?.is_admin) {
      const userMsg = { role: 'user', content: text, id: Date.now() };
      const draftMsg = { role: 'broadcast_draft', content: '', id: Date.now() + 1 };
      setMessages(prev => [...prev, userMsg, draftMsg]);
      setInput('');
      setLoading(true);
      const mailInit = [{ label: 'Thinking', done: true }, { label: 'Drafting email', done: false }];
      stepsRef.current = mailInit;
      setStatusSteps(mailInit);
      setBroadcastDraft({ drafting: true, originalMessage: text });
      chatIntentBroadcast(text)
        .then(r => {
          setBroadcastDraft({ ...r.data, originalMessage: text, drafting: false });
          stepsRef.current = [];
          setStatusSteps([]);
          setLoading(false);
        })
        .catch(() => {
          setBroadcastDraft(prev => ({ ...prev, drafting: false, error: 'AI draft failed. Try again.' }));
          stepsRef.current = [];
          setStatusSteps([]);
          setLoading(false);
        });
      return;
    }

    if (_isDocOnlyRequest(text)) {
      // Find the sources used for the last assistant message
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
      const sources = lastAssistant?.selectedSources || [];
      const userMsg = { role: 'user', content: text, id: Date.now() };
      setMessages(prev => [...prev, userMsg]);
      setInput('');
      setLoading(true);
      sendAndAppend(text, activeId, [...messages, userMsg], sources, true /* isDocRequest */);
      return;
    }

    if (quotaExhausted) return;
    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingQuestion(text);
  }, [loading, pendingQuestion, quotaExhausted, quota, messages, activeId, sendAndAppend]); // eslint-disable-line

  // Step 2: user confirmed which sources to search — actually send it.
  const confirmSources = useCallback(async (sources) => {
    const text = pendingQuestion;
    setPendingQuestion(null);
    setLoading(true);

    let convId = activeId;
    if (!convId) {
      try { const { data } = await newConversation(); convId = data.id; setActiveId(convId); }
      catch { setLoading(false); return; }
    }
    await sendAndAppend(text, convId, messages, sources);
  }, [pendingQuestion, activeId, messages, sendAndAppend]);

  // Step 2b: a disambiguation quick-reply chip — skips the source picker,
  // reuses the sources the original ambiguous question used.
  const quickReply = useCallback(async (text, sources, sourceMsgId) => {
    if (loading || quotaExhausted) return;
    if (sourceMsgId != null) {
      setMessages(prev => prev.map(m => (m.id === sourceMsgId ? { ...m, quickReplyUsed: text } : m)));
    }
    const userMsg = { role: 'user', content: text, id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    let convId = activeId;
    if (!convId) {
      try { const { data } = await newConversation(); convId = data.id; setActiveId(convId); }
      catch { setLoading(false); return; }
    }
    await sendAndAppend(text, convId, [...messages, userMsg], sources);
  }, [loading, quotaExhausted, activeId, messages, sendAndAppend]);

  const cancelSourcePicker = useCallback(() => {
    setPendingQuestion(null);
    setMessages(prev => prev.filter((m, i) => !(i === prev.length - 1 && m.role === 'user')));
  }, []);

  const value = {
    conversations, activeId, messages, setMessages, input, setInput, loading, initializing, statusSteps, pendingQuestion,
    quota, quotaExhausted, darkTheme, setDarkTheme,
    loadQuota, loadConversations, openConversation, newChat, deleteConv, renameConv,
    askQuestion, confirmSources, quickReply, cancelSourcePicker,
    broadcastDraft, confirmChatBroadcast, discardChatBroadcast, updateChatBroadcastBody, updateChatBroadcastSubject,
    meetDraft, confirmChatMeet, discardChatMeet,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used inside <ChatProvider>');
  return ctx;
}
